import { useMemo, useState } from 'react'
import { Calendar, ChevronDown, Download, Search } from 'lucide-react'
import { Spinner, formatDate, useToast } from '@dogdisk/shared'
import { AgentLayout } from '../components/agent-layout'
import {
  useMyTransactions,
  type AgentTxRow,
} from '../_lib/agent-api'

// 颜色规则与 .pen YVU0q 保持一致
const REF_TYPE_BADGE: Record<string, { text: string; bg: string; fg: string }> = {
  vip: { text: 'VIP 充值', bg: '#FEF3C7', fg: '#92400E' },
  vip_recharge: { text: 'VIP 充值', bg: '#FEF3C7', fg: '#92400E' },
  pack: { text: '资源包订阅', bg: '#DBEAFE', fg: '#1E40AF' },
  resource_pack: { text: '资源包订阅', bg: '#DBEAFE', fg: '#1E40AF' },
  content: { text: '内容奖励', bg: '#D1FAE5', fg: '#065F46' },
  reward: { text: '内容奖励', bg: '#D1FAE5', fg: '#065F46' },
}

const LEVEL_BADGE: Record<string, { bg: string; fg: string }> = {
  l1: { bg: '#DBEAFE', fg: '#1D4ED8' },
  l2: { bg: '#EDE9FE', fg: '#6D28D9' },
  l3: { bg: '#FFEDD5', fg: '#9A3412' },
}

const PAGE_SIZE = 10

export function AgentTransactionsPage() {
  const { data, isLoading } = useMyTransactions(500)
  const toast = useToast()

  const [page, setPage] = useState(1)
  const [refType, setRefType] = useState<string>('all')
  const [search, setSearch] = useState('')

  const all = data?.transactions ?? []
  const filtered = useMemo(() => {
    return all.filter((tx) => {
      if (refType !== 'all' && tx.reference_type !== refType) return false
      if (search && !tx.from_user_username_masked.toLowerCase().includes(search.toLowerCase()))
        return false
      return true
    })
  }, [all, refType, search])

  const total = filtered.length
  const pageItems = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))

  // 两卡均由当前筛选后的列表客户端聚合(spec YVU0q):列表总分成 + 当日分成。
  const listCommission = useMemo(
    () => filtered.reduce((s, tx) => s + tx.amount_coin, 0),
    [filtered],
  )
  const todayCommission = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10)
    return filtered
      .filter((tx) => tx.created_at.slice(0, 10) === today)
      .reduce((s, tx) => s + tx.amount_coin, 0)
  }, [filtered])

  const onExport = () => {
    if (filtered.length === 0) {
      toast.push('暂无可导出数据', { variant: 'warning' })
      return
    }
    const header = ['时间', 'reference_id', 'reference_type', '来源用户', '金额', '层级']
    const rows = filtered.map((tx) =>
      [
        formatDate(tx.created_at),
        tx.reference_id,
        tx.reference_type,
        tx.from_user_username_masked,
        tx.amount_coin.toString(),
        tx.level,
      ].join(','),
    )
    const csv = `﻿${header.join(',')}\n${rows.join('\n')}`
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `agent-transactions-${Date.now()}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <AgentLayout>
      <div data-testid="YVU0q" className="flex flex-col gap-5 p-6">
        {/* KPI 2 列(对齐 YVU0q) */}
        <section className="grid grid-cols-2 gap-4">
          <BigKpi
            label="列表总分成(当前筛选条件下)"
            value={listCommission.toLocaleString()}
          />
          <BigKpi label="当日分成" value={todayCommission.toLocaleString()} />
        </section>

        {/* 筛选栏 */}
        <section className="flex flex-wrap items-center gap-3">
          <div className="inline-flex h-9 items-center gap-2 rounded-md border border-gray-300 bg-white px-3 text-[13px] text-gray-600">
            <Calendar size={14} />
            <span>近 30 天</span>
          </div>

          <select
            value={refType}
            onChange={(e) => {
              setRefType(e.target.value)
              setPage(1)
            }}
            className="h-9 rounded-md border border-gray-300 bg-white px-3 pr-8 text-[13px] text-gray-700 outline-none"
          >
            <option value="all">全部类型</option>
            <option value="vip">VIP 充值</option>
            <option value="pack">资源包订阅</option>
            <option value="content">内容奖励</option>
          </select>

          <div className="inline-flex h-9 items-center gap-2 rounded-md border border-gray-300 bg-white px-3">
            <Search size={14} className="text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value)
                setPage(1)
              }}
              placeholder="来源用户搜索"
              className="w-40 bg-transparent text-[13px] text-gray-700 outline-none placeholder:text-gray-400"
            />
          </div>

          <div className="ml-auto" />

          <button
            type="button"
            onClick={onExport}
            className="inline-flex h-9 items-center gap-1.5 rounded-md px-4 text-[13px] font-medium text-white"
            style={{ backgroundColor: '#F59E0B' }}
          >
            <Download size={14} />
            导出 CSV
          </button>
        </section>

        {/* 表格 */}
        <section className="overflow-hidden rounded-xl border border-gray-200 bg-white">
          {isLoading ? (
            <div className="p-10">
              <Spinner className="text-amber-500" />
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="border-b border-gray-200 bg-gray-50">
                <tr className="text-left text-xs font-semibold text-gray-500">
                  <th className="px-4 py-3 w-[160px]">时间</th>
                  <th className="px-4 py-3 w-[140px]">reference_id</th>
                  <th className="px-4 py-3 w-[120px]">reference_type</th>
                  <th className="px-4 py-3 w-[130px]">来源用户</th>
                  <th className="px-4 py-3 w-[110px]">金额</th>
                  <th className="px-4 py-3">层级</th>
                </tr>
              </thead>
              <tbody>
                {pageItems.map((tx) => (
                  <TxRow key={tx.id} tx={tx} />
                ))}
                {pageItems.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-4 py-12 text-center text-sm text-gray-400">
                      暂无分账流水
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}

          {/* 分页 */}
          <div className="flex items-center justify-between border-t border-gray-200 px-4 py-3">
            <span className="text-[13px] text-gray-500">共 {total} 条</span>
            <Pager page={page} total={totalPages} onChange={setPage} />
          </div>
        </section>
      </div>
    </AgentLayout>
  )
}

function BigKpi({ label, value, note }: { label: string; value: string; note?: string }) {
  return (
    <div className="flex flex-col gap-2 rounded-xl border border-gray-200 bg-white px-6 py-5">
      <span className="text-[13px] text-gray-500">{label}</span>
      <span className="flex items-baseline gap-1.5">
        <span className="text-[28px] font-bold tabular-nums text-gray-900">{value}</span>
        <span className="text-sm font-medium text-amber-500">COIN</span>
      </span>
      {note && <span className="text-[11px] text-gray-400">{note}</span>}
    </div>
  )
}

function TxRow({ tx }: { tx: AgentTxRow }) {
  const refMeta = REF_TYPE_BADGE[tx.reference_type] ?? {
    text: tx.reference_type,
    bg: '#F3F4F6',
    fg: '#374151',
  }
  const lvMeta = LEVEL_BADGE[tx.level.toLowerCase()] ?? { bg: '#F3F4F6', fg: '#374151' }
  return (
    <tr className="border-b border-gray-100 last:border-b-0">
      <td className="px-4 py-3 text-[13px] text-gray-700">{formatDate(tx.created_at)}</td>
      <td className="px-4 py-3 font-mono text-[13px] text-blue-600">
        {shortenRefId(tx.reference_id)}
      </td>
      <td className="px-4 py-3">
        <span
          className="inline-block rounded px-2 py-0.5 text-xs font-medium"
          style={{ backgroundColor: refMeta.bg, color: refMeta.fg }}
        >
          {refMeta.text}
        </span>
      </td>
      <td className="px-4 py-3 text-[13px] text-gray-700">{tx.from_user_username_masked}</td>
      <td className="px-4 py-3 text-[13px] font-medium tabular-nums text-emerald-600">
        +{tx.amount_coin.toFixed(2)} COIN
      </td>
      <td className="px-4 py-3">
        <span
          className="inline-block rounded px-2 py-0.5 text-xs font-medium uppercase"
          style={{ backgroundColor: lvMeta.bg, color: lvMeta.fg }}
        >
          {tx.level}
        </span>
      </td>
    </tr>
  )
}

function shortenRefId(id: string): string {
  if (id.length <= 10) return id
  return `${id.slice(0, 4)}…${id.slice(-4)}`
}

function Pager({
  page,
  total,
  onChange,
}: {
  page: number
  total: number
  onChange: (p: number) => void
}) {
  const goPrev = () => page > 1 && onChange(page - 1)
  const goNext = () => page < total && onChange(page + 1)
  const pages = computePagerWindow(page, total)
  return (
    <div className="flex items-center gap-1">
      <PagerBtn onClick={goPrev} disabled={page <= 1} bordered>
        <ChevronDown size={14} className="rotate-90" />
      </PagerBtn>
      {pages.map((p, i) =>
        p === '…' ? (
          <span key={`d${i}`} className="px-1 text-[13px] text-gray-400">
            …
          </span>
        ) : (
          <PagerBtn
            key={p}
            onClick={() => onChange(p)}
            active={p === page}
            bordered={p !== page}
          >
            {p}
          </PagerBtn>
        ),
      )}
      <PagerBtn onClick={goNext} disabled={page >= total} bordered>
        <ChevronDown size={14} className="-rotate-90" />
      </PagerBtn>
    </div>
  )
}

function PagerBtn({
  children,
  onClick,
  disabled,
  active,
  bordered,
}: {
  children: React.ReactNode
  onClick?: () => void
  disabled?: boolean
  active?: boolean
  bordered?: boolean
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={[
        'flex h-8 min-w-8 items-center justify-center rounded-md px-2 text-[13px] font-medium transition-colors',
        active ? 'text-white' : bordered ? 'border border-gray-300 text-gray-600 hover:bg-gray-50' : 'text-gray-600',
        disabled ? 'opacity-40' : '',
      ].join(' ')}
      style={active ? { backgroundColor: '#F59E0B' } : undefined}
    >
      {children}
    </button>
  )
}

function computePagerWindow(page: number, total: number): Array<number | '…'> {
  if (total <= 5) return Array.from({ length: total }, (_, i) => i + 1)
  if (page <= 3) return [1, 2, 3, '…', total]
  if (page >= total - 2) return [1, '…', total - 2, total - 1, total]
  return [1, '…', page - 1, page, page + 1, '…', total]
}
